/**
 * AppHeader — top bar for the Chemelex Agentic SIOP cockpit.
 *
 * Left: Chemelex wordmark + "Agentic SIOP" label.
 * Right: welcome + account menu.
 */
import { Divider, Popover, Typography } from '@mui/material';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import KeyIcon from '@mui/icons-material/Key';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthContext } from '../../context/AuthProvider';
import chemelexLogo from '../../assets/chemelex-logo.webp';
import ChangePasswordModal from './ChangePasswordModal';

const CONTAINER_STYLE = {
  display: 'flex',
  alignItems: 'center',
  padding: '4px 24px',
  borderBottom: '1px solid rgba(159, 159, 159, 0.2)',
  backgroundColor: '#fff',
  minHeight: 48,
  maxHeight: 48,
};

const ICON_HOVER = { '&:hover': { color: '#FD5707' }, cursor: 'pointer', color: '#404040' };

// One row of the account popover. Shared by every row so Settings, Change
// Password and Sign Out cannot drift apart.
const MENU_ROW_SX = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  px: 1,
  py: 0.75,
  width: '100%',
  border: 'none',
  background: 'none',
  font: 'inherit',
  textAlign: 'left',
  cursor: 'pointer',
  borderRadius: 1,
  '&:hover': { backgroundColor: 'rgba(253,87,7,0.1)' },
};

function Header({ onLogout }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const [signingOut, setSigningOut] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const { authState, logout, checkPermission } = useAuthContext();
  const navigate = useNavigate();
  // Only Admin holds USER_MGMT (rbac.constants.js, BRD §4). Everyone else would
  // land on a Settings page with no tabs, so the entry point is withheld.
  const canManageUsers = checkPermission('USER_MGMT', 'read');

  const handleMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleMenuClose = () => setAnchorEl(null);
  const open = Boolean(anchorEl);

  // Delegates to the context, which revokes the session on the server, clears
  // the token store and redirects. This used to do its own partial teardown
  // (sessionStorage only) and never told the backend, so the refresh cookie
  // stayed valid and a reload restored the session from localStorage.
  const handleLogout = async () => {
    if (signingOut) return; // double-click would fire two /logout calls
    setSigningOut(true);
    setAnchorEl(null);
    try {
      await logout();
      onLogout?.();
    } catch {
      // logout() already swallows the API call's failure and tears the session
      // down regardless, so reaching here means something unexpected. Swallow it
      // rather than let it escape as an unhandled rejection, and re-enable the
      // button below so the user can try again.
    } finally {
      setSigningOut(false);
    }
  };

  return (
    <>
      <div style={CONTAINER_STYLE}>
        {/* ── Left: logo slot ─────────────────────────────────── */}
        <div style={{ flex: 1.5, display: 'flex', alignItems: 'center', gap: 8 }}>
          <Box
            component="img"
            src={chemelexLogo}
            alt="Chemelex"
            sx={{ height: 24, width: 'auto', display: 'block' }}
          />

          <Divider
            orientation="vertical"
            variant="middle"
            flexItem
            sx={{ height: 22, borderColor: '#E8602C', mx: 0.5 }}
          />

          <Typography sx={{ fontWeight: 600, fontSize: 14, color: '#444' }}>
            Agentic SIOP
          </Typography>
        </div>

        {/* The centre slot held the SIOP status chip, its quarter legend and
            the alerts bell. All three were removed; the slot goes with them
            rather than staying as an empty flex child, so the account menu
            keeps its own right-alignment. AlertsBell.jsx stays on disk,
            unreferenced — restore it here to bring alerts back. */}

        {/* ── Right: welcome + account menu ──────────────────────── */}
        <div
          style={{
            flex: 1,
            display: 'flex',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: 10,
          }}
        >
          {/* Today's date, computed on render. The header is mounted for the
              whole session, so a tab left open overnight would show yesterday
              until something else re-rendered it — acceptable for a greeting
              line, and cheaper than a ticking timer. */}
          <Typography sx={{ fontWeight: 500, fontSize: 13, color: '#404040' }}>
            <Box component="span" sx={{ color: '#6B7280' }}>
              {new Date().toLocaleDateString('en-US', {
                weekday: 'short',
                day: 'numeric',
                month: 'short',
                year: 'numeric',
              })}
            </Box>
            <Box component="span" sx={{ color: '#D1D5DB', mx: 1 }}>
              |
            </Box>
            Welcome! {authState?.userInfo?.firstName || 'User'}
          </Typography>

          <IconButton onClick={handleMenuOpen} sx={ICON_HOVER} size="small">
            <AccountCircleIcon />
            <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </div>
      </div>

      {/* ── Account popover ─────────────────────────────────────── */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ p: 1, minWidth: 160 }}>
          {canManageUsers && (
            <Box
              component="button"
              type="button"
              sx={MENU_ROW_SX}
              onClick={() => {
                setAnchorEl(null);
                navigate('/settings');
              }}
            >
              <SettingsIcon sx={{ fontSize: 18, color: '#404040' }} />
              <Typography sx={{ fontSize: 13, color: '#404040' }}>Settings</Typography>
            </Box>
          )}
          {/* Every signed-in user — it is their own password, so unlike
              Settings it is not gated. It opens a modal over the current page
              rather than navigating (TASK-2026-10-12-002). */}
          <Box
            component="button"
            type="button"
            sx={MENU_ROW_SX}
            onClick={() => {
              setAnchorEl(null);
              setChangePasswordOpen(true);
            }}
          >
            <KeyIcon sx={{ fontSize: 18, color: '#404040' }} />
            <Typography sx={{ fontSize: 13, color: '#404040' }}>Change Password</Typography>
          </Box>
          <Box
            component="button"
            type="button"
            disabled={signingOut}
            sx={{ ...MENU_ROW_SX, cursor: signingOut ? 'wait' : 'pointer' }}
            onClick={handleLogout}
          >
            <LogoutIcon sx={{ fontSize: 18, color: '#404040' }} />
            <Typography sx={{ fontSize: 13, color: '#404040' }}>
              {signingOut ? 'Signing Out…' : 'Sign Out'}
            </Typography>
          </Box>
        </Box>
      </Popover>

      <ChangePasswordModal open={changePasswordOpen} onClose={() => setChangePasswordOpen(false)} />
    </>
  );
}

export default Header;
